import { useState, useEffect } from 'react';

export interface Reminder {
  id: string;
  tenant_id: string;
  entity_type: string | null;
  entity_id: string | null;
  reminder_type: string;
  reminder_date: string;
  message: string;
  is_sent: boolean;
  is_dismissed: boolean;
  created_at: string;
}

const REMINDER_TYPES = [
  { value: 'upcoming_shoot',  label: 'Upcoming Shoot',    icon: 'ri-camera-3-line' },
  { value: 'payment_pending', label: 'Payment Pending',   icon: 'ri-money-dollar-circle-line' },
  { value: 'photo_selection', label: 'Photo Selection',   icon: 'ri-image-2-line' },
  { value: 'contract_due',    label: 'Contract Due',      icon: 'ri-file-text-line' },
  { value: 'custom',          label: 'Custom',            icon: 'ri-alarm-line' },
];

const ENTITY_TYPES = [
  { value: 'job',      label: 'Job / Shoot' },
  { value: 'contract', label: 'Contract' },
  { value: 'payment',  label: 'Payment' },
  { value: 'client',   label: 'Client' },
];

type FormData = {
  reminder_type: string;
  entity_type: string;
  reminder_date: string;
  message: string;
};

interface Props {
  reminder: Reminder | null;
  isNew: boolean;
  onClose: () => void;
  onSave: (data: FormData) => void;
}

const blank = (): FormData => ({
  reminder_type: 'custom',
  entity_type:   '',
  reminder_date: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
  message:       '',
});

export default function ReminderModal({ reminder, isNew, onClose, onSave }: Props) {
  const [form, setForm] = useState<FormData>(blank());

  useEffect(() => {
    if (reminder) {
      setForm({
        reminder_type: reminder.reminder_type,
        entity_type:   reminder.entity_type ?? '',
        reminder_date: reminder.reminder_date.slice(0, 16),
        message:       reminder.message,
      });
    } else {
      setForm(blank());
    }
  }, [reminder]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.message.trim() || !form.reminder_date) return;
    onSave({ ...form, entity_type: form.entity_type || '' });
  };

  const selectedType = REMINDER_TYPES.find((t) => t.value === form.reminder_type);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-50 text-amber-500">
              <i className="ri-alarm-line text-sm"></i>
            </span>
            <h2 className="text-[#0f172a] font-semibold text-base">
              {isNew ? 'New Reminder' : 'Edit Reminder'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* Type */}
          <div>
            <label className="text-slate-500 text-xs mb-1.5 block">Reminder Type</label>
            <div className="grid grid-cols-3 gap-2">
              {REMINDER_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, reminder_type: t.value }))}
                  className={`flex flex-col items-center gap-1.5 px-2 py-2.5 rounded-xl border text-xs font-medium transition-all cursor-pointer ${
                    form.reminder_type === t.value
                      ? 'border-amber-300 bg-amber-50 text-amber-600'
                      : 'border-slate-200 bg-slate-50 text-slate-400 hover:text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${t.icon} text-sm`}></i>
                  </span>
                  <span className="text-center leading-tight">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Reminder Date */}
          <div>
            <label className="text-slate-500 text-xs mb-1.5 block">Reminder Date & Time *</label>
            <input
              name="reminder_date"
              type="datetime-local"
              value={form.reminder_date}
              onChange={handleChange}
              required
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Entity Type */}
          <div>
            <label className="text-slate-500 text-xs mb-1.5 block">
              Related To <span className="text-slate-300">(optional)</span>
            </label>
            <select
              name="entity_type"
              value={form.entity_type}
              onChange={handleChange}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer appearance-none"
            >
              <option value="">Not linked</option>
              {ENTITY_TYPES.map((e) => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div>
            <label className="text-slate-500 text-xs mb-1.5 block">Message *</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              required
              rows={3}
              maxLength={500}
              placeholder={
                selectedType?.value === 'upcoming_shoot' ? 'Upcoming shoot tomorrow — confirm everything is ready.'
                : selectedType?.value === 'payment_pending' ? 'Client has an outstanding balance. Follow up today.'
                : selectedType?.value === 'photo_selection' ? 'Client photo selection is still pending.'
                : 'Reminder message…'
              }
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {isNew ? 'Set Reminder' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}